/**
 * vl-expert-quotes.js — 首页「Hear from the Experts」媒体证言（子需求 08）
 *
 * 需求：需求/20260922-新版首页改版/子需求/09-首页HearFromTheExperts媒体证言/
 *
 * 行为（用户 2026-09-30 口径）：
 *   1) PC 右侧媒体 logo 是**竖直滑轮**：active 恒在竖直中心、可无限循环。
 *      实现：列表渲染 3 份（第 2/3 份 aria-hidden），滑窗高度 = 槽位数（默认 5）格；
 *      每推进一条，整条轨道**只带过渡上移一格**；当滑窗位置到达末尾时，**先瞬时退回 n 格**
 *      （三份内容周期重复，视觉完全一致），再走一格 —— 于是可以一直循环，且
 *      active 每次都落在中间格（.is-center）。第 3 份为「点击任意图标直接激活」提供向上/向下的位移富余。
 *   2) M 只显示当前 logo（.is-active），指示条为 [当前＝72×8 进度条][其余＝8×8 圆点]，
 *      进度条按停留时长填充（Apple timed dotnav 的观感）。
 *   3) 自动播放：按 data-vl-duration（秒，0.1 精度）推进；进入视口（IntersectionObserver）
 *      且页面可见时才走，离开视口/切后台自动停表。组件无手动切换控件（设计稿未提供）。
 *   4) 交互（用户 2026-09-30 追加）：**鼠标悬停在右侧滑轮上时暂停自动播放**（离开恢复）；
 *      **点击滑轮里的任一媒体 logo → 直接把它设为当前条**（整列滑动，使其落到竖直中心）。
 *   5) PC 右侧的竖条**是进度条**（用户 2026-10-08 修正）：填充高度＝**当前条内容的播放进度**
 *      （与 M 端进度条同义：填充高度＝已播放占比）；**播放满后先回缩到 0**（`data-vl-retract`
 *      毫秒，期间内容已切换），回缩完再开始下一条的增长；纯展示、不可交互（原「可拖拽/键盘
 *      的滑块」交互已按该口径移除）。
 *   6) 交互（用户 2026-10-12 口径变更）：**取消图标列拖拽**——手动切换只保留「点击滑轮里的媒体
 *      logo 直接把它设为当前条」（原来的按住拖动跟手、松手吸附、6px 拖拽/点击判定全部移除）。
 *   7) 键盘可达性（用户 2026-10-12 追加）：**PC** 滑轮的每个格子 = `role="button"` + `tabindex="0"`
 *      （由 `_syncWheelA11y()` 按断点挂/摘；M 端只静态展示当前条，不挂，避免死焦点位），
 *      **Enter / Space** 激活该条（走与点击同一条 `goToIndex` 路径），当前项带 `aria-current="true"`，
 *      可见焦点环见 CSS `:focus-visible`。
 *   8) **M 端左右滑动切换**（用户 2026-10-14 追加）：「手机端增加左右滑动切换的交互」，
 *      并明确「应该是跟正常那种滑块轮播一样**可以露出下一个**的那种」。
 *      实现：M 端把引语与 logo 各做成**一条横向轨道**（CSS：`.vl-eq__quotes` 由叠放改 flex 横排、
 *      `.vl-eq__wheel` 由竖排改横排且只用第 1 份），位置 = `-index×100% + 跟手像素`：
 *      `--vl-eq-slide-index`（整数张，由 `_syncText()` 维护）＋ `--vl-eq-drag`（拖拽像素）。
 *      ⇒ **拖的时候相邻那张会从边上滑进来**；松手 ≥ 阈值（卡片宽 15%，夹 32~64px）则切到相邻张
 *      （轨道带 0.28s 过渡继续滑到位），未达阈值回弹。首/末张处位移×0.25 阻尼（不露空白），
 *      且**滑动不绕回**（没有克隆张；无限循环由自动播放承担，PC 滑轮的循环语义不变）。
 *      手势绑在白卡上、`pointermove/up/cancel` 挂在 window（滑出卡片再松手也能收尾）；
 *      `touch-action: pan-y` ＋ 轴向判定 ⇒ 纵向手势仍交给页面滚动；拖拽期间暂停自动播放。
 *
 * 约定：IIFE + customElements 一次注册 + disconnectedCallback 清理（规范 §3.6）；
 *      位移通过 --vl-eq-shift 交给 CSS（PC 媒体查询里才应用），避免移动端误位移。
 */
(function () {
  'use strict';

  var SHIFT_MS = 600; // 与 CSS 的 transition 时长一致
  var SETTLE_MS = 120; // resize 后的重新度量防抖
  var MAX_TICK_MS = 250; // rAF 单帧最大计入时长（切后台回来不跳变）
  var DEFAULT_DURATION = 5; // 秒
  var COPY_COUNT = 3; // 轨道份数（与 sections/vl-expert-quotes.liquid 保持一致）

  class VLExpertQuotes extends HTMLElement {
    constructor() {
      super();
      this._onTick = this._onTick.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onPointerEnter = this._onPointerEnter.bind(this);
      this._onPointerLeave = this._onPointerLeave.bind(this);
      this._onWheelClick = this._onWheelClick.bind(this);
      this._onWheelKeydown = this._onWheelKeydown.bind(this);
      this._onBreakpointChange = this._onBreakpointChange.bind(this);
      this._onSwipeDown = this._onSwipeDown.bind(this);
      this._onSwipeMove = this._onSwipeMove.bind(this);
      this._onSwipeUp = this._onSwipeUp.bind(this);
    }

    connectedCallback() {
      if (this._ready) {
        // 被重新接回 DOM（例如编辑器里移动 section）：只重新绑定监听/观察者，不重置状态
        if (this._detached) {
          this._detached = false;
          this._observe();
          this._bindPointer();
          this._syncWheelA11y();
          if (this.mqDesktop && this.mqDesktop.addEventListener) {
            this.mqDesktop.addEventListener('change', this._onBreakpointChange);
          }
          document.addEventListener('visibilitychange', this._onVisibility);
          window.addEventListener('resize', this._onResize);
          window.addEventListener('load', this._onResize);
          this._applyShift(true);
          this._evaluate();
        }
        return;
      }
      this._ready = true;

      this.wheel = this.querySelector('[data-vl-wheel]');
      this.texts = Array.prototype.slice.call(this.querySelectorAll('[data-vl-quote]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-vl-dot]'));
      this.fills = this.dots.map(function (dot) {
        return dot.querySelector('i');
      });
      this.railFill = this.querySelector('[data-vl-rail-fill]');
      this.viewport = this.querySelector('.vl-eq__viewport');
      this.card = this.querySelector('.vl-eq__card');
      this.mqDesktop =
        window.matchMedia && typeof window.matchMedia === 'function'
          ? window.matchMedia('(min-width: 768px)')
          : null;
      this.mqMobile =
        window.matchMedia && typeof window.matchMedia === 'function'
          ? window.matchMedia('(max-width: 767px)')
          : null;

      this.count = this.texts.length;
      this.index = 0;
      this.step = 0;
      this.t = 0; // 滑窗位置（轨道第几格对齐到窗口顶部）
      this.progress = 0;
      this.elapsed = 0;
      this.last = 0;
      this.retracting = false; // 是否处于「播放满→进度条回缩」阶段
      this.playing = false;
      this.inView = false;
      this.hover = false; // 鼠标是否悬停在右侧滑轮上
      this.dragging = false; // M 端是否正在横向拖拽（拖拽期间暂停自动播放）
      this.dragAxis = ''; // '' | 'x' | 'y'
      this.dragStart = null;
      this.dragX = 0;
      this.dragDelta = 0; // 实际生效的位移（含首/末张阻尼），供「是否切换」判定
      this.raf = null;
      this.duration = this._readDuration();
      this.retractMs = this._readRetract();
      this.autoplay = this.dataset.vlAutoplay !== 'false';
      this.reduced = !!(
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );

      var slots = parseInt(this.style.getPropertyValue('--vl-eq-slots'), 10);
      if (!slots || slots < 1) slots = Math.min(this.count, 5) || 1;
      this.slots = Math.min(slots, this.count || 1);
      // active 恒在窗口的中间格
      this.mid = Math.floor(this.slots / 2);

      if (this.count > 1) {
        // 起始滑窗位置：让第 0 条正好落在中间格
        this.t = (((this.index - this.mid) % this.count) + this.count) % this.count;
      }

      this._syncText();
      this._syncDots();
      this._setActive();
      this._syncCenter();
      this._setRailFill(0);
      this._applyShift(true);
      if (this.count < 2) {
        this._syncProgress(1);
        this._setRailFill(1);
      }

      this._observe();
      this._bindPointer();
      this._bindSwipe();
      this._syncWheelA11y();
      if (this.mqDesktop && this.mqDesktop.addEventListener) {
        this.mqDesktop.addEventListener('change', this._onBreakpointChange);
      }
      document.addEventListener('visibilitychange', this._onVisibility);
      window.addEventListener('resize', this._onResize);
      window.addEventListener('load', this._onResize);
    }

    disconnectedCallback() {
      this._detached = true;
      this._stop();
      window.clearTimeout(this._resizeTimer);
      window.clearTimeout(this._retractTimer);
      if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
      if (this.viewport) {
        this.viewport.removeEventListener('pointerenter', this._onPointerEnter);
        this.viewport.removeEventListener('pointerleave', this._onPointerLeave);
        this.viewport.removeEventListener('click', this._onWheelClick);
        this.viewport.removeEventListener('keydown', this._onWheelKeydown);
      }
      this._unbindSwipe();
      if (this.mqDesktop && this.mqDesktop.removeEventListener) {
        this.mqDesktop.removeEventListener('change', this._onBreakpointChange);
      }
      document.removeEventListener('visibilitychange', this._onVisibility);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('load', this._onResize);
    }

    /* ---------------------------------------------------------------- 计时 */
    /* 回缩时长（毫秒）：播放满后进度条从 100% 缩回 0 的动画时长 */
    _readRetract() {
      var value = parseInt(this.dataset.vlRetract, 10);
      if (!value || value <= 0 || !isFinite(value)) value = 300;
      return value;
    }

    _readDuration() {
      var value = parseFloat(this.dataset.vlDuration);
      if (!value || value <= 0 || !isFinite(value)) value = DEFAULT_DURATION;
      return value;
    }

    _observe() {
      var self = this;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].target === self) self.inView = entries[i].isIntersecting;
            }
            self._evaluate();
          },
          { threshold: 0.35 }
        );
        this.io.observe(this);
      } else {
        this.inView = true;
        this._evaluate();
      }
    }

    _onVisibility() {
      this._evaluate();
    }

    _evaluate() {
      var should =
        this.autoplay &&
        this.count > 1 &&
        this.inView &&
        !this.dragging &&
        !this.hover &&
        document.visibilityState !== 'hidden';
      if (should) this._play();
      else this._stop();
    }

    /* ---------------------------------------------------------------- 指针交互 */
    _bindPointer() {
      if (!this.viewport) return;
      this.viewport.addEventListener('pointerenter', this._onPointerEnter);
      this.viewport.addEventListener('pointerleave', this._onPointerLeave);
      this.viewport.addEventListener('click', this._onWheelClick);
      this.viewport.addEventListener('keydown', this._onWheelKeydown);
    }

    /* M 端「白卡左右滑动切换」的监听（用户 2026-10-14）：
       ① `pointerdown` 绑在白卡上（手势从卡上开始）；
       ② 一旦按下，`pointermove/up/cancel` 就挂到 **window** —— 这样手指滑出卡片再松开也能收到 pointerup，
          不会出现「拖着拖着松手在卡外 ⇒ dragging 卡住 ⇒ 自动播放停摆、进度条不走」的问题
          （2026-10-14 用户反馈的根因，实测复现：松手在卡外时 playing=false、elapsed 不涨）；
       ③ 另加 8s 守护定时器兜底（万一事件流被系统中断），到点强制结束拖拽。 */
    _bindSwipe() {
      if (!this.card) return;
      this.card.addEventListener('pointerdown', this._onSwipeDown);
    }

    _unbindSwipe() {
      if (this.card) this.card.removeEventListener('pointerdown', this._onSwipeDown);
      this._endDrag();
    }

    _attachDragListeners() {
      if (this._dragBound) return;
      this._dragBound = true;
      window.addEventListener('pointermove', this._onSwipeMove, { passive: false });
      window.addEventListener('pointerup', this._onSwipeUp);
      window.addEventListener('pointercancel', this._onSwipeUp);
    }

    _detachDragListeners() {
      if (!this._dragBound) return;
      this._dragBound = false;
      window.removeEventListener('pointermove', this._onSwipeMove);
      window.removeEventListener('pointerup', this._onSwipeUp);
      window.removeEventListener('pointercancel', this._onSwipeUp);
    }

    /* 结束拖拽：复位一切状态并**恢复自动播放**（所有退出路径都必须走这里） */
    _endDrag() {
      window.clearTimeout(this._dragWatchdog);
      this._dragWatchdog = null;
      this._detachDragListeners();
      this.dragging = false;
      this.dragAxis = '';
      this.dragStart = null;
      this.dragX = 0;
      this.dragDelta = 0;
      this.classList.remove('is-dragging');
      this.style.setProperty('--vl-eq-drag', '0px');
      this._evaluate();
    }

    /**
     * 悬停右侧滑轮 → 暂停；移开 → 恢复。
     * 仅 PC（滑轮存在的一侧）；且只在真实鼠标（pointerType === 'mouse'）时生效，
     * 避免触摸设备上"按一下就永久暂停"（触摸不会触发 pointerleave）。
     */
    _onPointerEnter(event) {
      if (event && event.pointerType && event.pointerType !== 'mouse') return;
      if (this.mqDesktop && !this.mqDesktop.matches) return;
      this.hover = true;
      this._evaluate();
    }

    _onPointerLeave(event) {
      if (event && event.pointerType && event.pointerType !== 'mouse') return;
      this.hover = false;
      this._evaluate();
    }

    /* 点击滑轮里的某个媒体 logo → 直接把它设为当前条
       （用户 2026-10-12 口径：**取消图标列拖拽**，只保留这一种手动切换方式） */
    _onWheelClick(event) {
      var node = event.target;
      var item = node && node.closest ? node.closest('.vl-eq__logo') : null;
      if (!item || !this.wheel || !this.wheel.contains(item)) return;
      this.goToIndex(parseInt(item.dataset.vlIndex, 10), item);
    }

    /* 断点跨越 768 时重挂/摘滑轮的键盘可达性 */
    _onBreakpointChange() {
      this._syncWheelA11y();
      // M 端滑动只改了 index（滑窗位置没动）⇒ 切回 PC 时把滑轮对齐到当前条
      if (this.mqDesktop && this.mqDesktop.matches && this.count > 1) this._alignWheelToIndex();
    }

    /* ------------------------------------------------ M 端左右滑动切换（用户 2026-10-14） */

    _onSwipeDown(event) {
      if (this.count < 2) return;
      if (this.mqMobile && !this.mqMobile.matches) return; // 仅 M 端
      if (typeof event.button === 'number' && event.button !== 0) return;
      if (this.dragStart) return; // 已有手势在进行（多指等）⇒ 忽略新的按下
      this.dragging = true;
      this.dragAxis = '';
      this.dragX = 0;
      this.dragStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      this._attachDragListeners();
      window.clearTimeout(this._dragWatchdog);
      var self = this;
      this._dragWatchdog = window.setTimeout(function () {
        // 兜底：事件流被系统中断（例如来电/手势被系统接管）时强制收尾，避免自动播放永久停摆
        if (self.dragging) self._endDrag();
      }, 8000);
      this._evaluate(); // 拖拽期间暂停自动播放
    }

    _onSwipeMove(event) {
      if (!this.dragging || !this.dragStart) return;
      if (this.dragStart.id !== undefined && event.pointerId !== this.dragStart.id) return;

      var dx = event.clientX - this.dragStart.x;
      var dy = event.clientY - this.dragStart.y;

      // 轴向判定：先动过 8px 才定方向；纵向 ⇒ 立刻放弃拖拽，把滚动交回页面
      if (!this.dragAxis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        this.dragAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (this.dragAxis === 'y') {
          this._endDrag(); // 纵向手势：交回页面滚动
          return;
        }
        this.classList.add('is-dragging');
      }
      if (this.dragAxis !== 'x') return;

      this.dragX = dx;
      // ① 首/末张的边界阻尼：没有上一张/下一张时不让轨道露出空白（位移压到 25%）
      var edge = (this.index <= 0 && dx > 0) || (this.index >= this.count - 1 && dx < 0);
      var applied = edge ? dx * 0.25 : dx;
      // ② 阻尼：拖过卡片宽度后继续减半，避免把整块拖出画面
      var w = this.card ? this.card.offsetWidth : 0;
      if (w > 0 && Math.abs(applied) > w) {
        applied = applied > 0 ? w + (applied - w) * 0.4 : -w + (applied + w) * 0.4;
      }
      this.dragDelta = applied; // 切换判定用「实际生效位移」，越界阻尼不会误触发切换
      this.style.setProperty('--vl-eq-drag', Math.round(applied) + 'px');
      if (event.cancelable) event.preventDefault();
    }

    _onSwipeUp(event) {
      if (!this.dragStart) return;
      if (this.dragStart.id !== undefined && event && event.pointerId !== undefined && event.pointerId !== this.dragStart.id) return;

      var wasHorizontal = this.dragAxis === 'x';
      var delta = this.dragDelta; // 用「实际生效位移」判定（首/末张的越界阻尼不会误触发）
      this._endDrag(); // 复位 + 恢复自动播放（无论松手在卡片内还是卡片外）

      if (wasHorizontal && Math.abs(delta) >= this._readSwipeThreshold()) {
        this._goRelative(delta < 0 ? 1 : -1); // 左滑＝下一条，右滑＝上一条
      }
    }

    /* 切换阈值：卡片宽 15%，夹在 32~64px（M 卡宽 358 ⇒ 54px） */
    _readSwipeThreshold() {
      var w = this.card ? this.card.offsetWidth : 0;
      return Math.max(32, Math.min(64, Math.round(w * 0.15)));
    }

    /**
     * 相对切换（环形）。M 端滑动走这里；
     * 若当前在 PC 断点（例如窄窗口用鼠标拖），则走既有 goToIndex 以同时对齐滑轮。
     */
    _goRelative(delta) {
      var n = this.count;
      if (n < 2 || !delta) return;
      // M 端轨道不做首尾循环（没有克隆张，绕回会「倒带」扫过全部内容）⇒ 首/末张处夹住；
      // 无限循环由自动播放承担（PC 滑轮的循环语义不变）。
      var next = Math.max(0, Math.min(n - 1, this.index + delta));
      if (next === this.index) {
        this._evaluate();
        return;
      }
      if (this.mqDesktop && this.mqDesktop.matches) {
        this.goToIndex(next);
        return;
      }
      this.index = next;
      this.elapsed = 0;
      this.progress = 0;
      this.last = 0;
      this._setActive();
      this._syncText();
      this._syncDots();
      this._syncProgress(0);
      this._alignWheelToIndex(); // 保持滑窗与 index 一致（跨回 PC 不跳）
      this._retractFill(); // 与「点击滑轮切换」同一语义：先回缩再增长
    }

    /* 把滑轮滑窗对齐到当前 index（不碰进度），用于 M 端滑动后 / 断点切回 PC 时 */
    _alignWheelToIndex() {
      var n = this.count;
      if (!this.wheel || n < 2) return;
      this.t = (((this.index - this.mid) % n) + n) % n;
      this._syncCenter();
      this._applyShift(true);
    }

    /* 键盘：Enter / Space 激活焦点所在的格子（走与点击同一条路径） */
    _onWheelKeydown(event) {
      var key = event.key;
      if (key !== 'Enter' && key !== ' ' && key !== 'Spacebar') return;
      var item = event.target && event.target.closest ? event.target.closest('.vl-eq__logo') : null;
      if (!item || item.classList.contains('vl-eq__logo--dup')) return;
      if (!this.wheel || !this.wheel.contains(item)) return;
      event.preventDefault(); // Space 默认会滚动页面
      this.goToIndex(parseInt(item.dataset.vlIndex, 10), item);
    }

    /**
     * 滑轮的键盘可达性（用户 2026-10-12）。
     * 只有 **PC** 的滑轮是「可点选的控件」：M 端仅静态展示当前条（其余格子 display:none）
     * ⇒ 在非 PC 端摘掉 role/tabindex，避免多出一个"按了没反应"的焦点位。
     * 当前项用 `aria-current="true"` 表达（由 `_setActive()` 维护）。
     */
    _syncWheelA11y() {
      if (!this.wheel) return;
      var desktop = !this.mqDesktop || this.mqDesktop.matches;
      var kids = this.wheel.children;
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        if (el.classList.contains('vl-eq__logo--dup')) continue; // 复制副本对读屏隐藏，不参与
        if (desktop) {
          el.setAttribute('role', 'button');
          el.setAttribute('tabindex', '0');
        } else {
          el.removeAttribute('role');
          el.removeAttribute('tabindex');
        }
      }
    }

    /**
     * 切换到指定内容条（并让它滑到竖直中心）。
     * @param {number} index 目标条（0 基，逻辑序号）
     * @param {HTMLElement} [item] 被点击的那个 li（优先用它，保证滑动方向与用户点击位置一致）
     */
    goToIndex(index, item) {
      var n = this.count;
      if (n < 2 || !isFinite(index) || index < 0 || index >= n) return;
      var kids = this.wheel.children;
      var target = item && this.wheel.contains(item) ? item : null;
      if (!target) {
        // 没有指定元素时（键盘 / 外部调用）：在三份副本里挑离当前位置最近的同序号元素，保证位移最小
        var best = Infinity;
        for (var i = 0; i < kids.length; i++) {
          if (parseInt(kids[i].dataset.vlIndex, 10) !== index) continue;
          var dist = Math.abs(i - this.mid - this.t);
          if (dist < best) {
            best = dist;
            target = kids[i];
          }
        }
      }
      if (!target) return;
      this.index = index;
      this._moveBy(Array.prototype.indexOf.call(kids, target) - this.mid - this.t);
      this._retractFill(); // 手动切换同样先回缩再增长
    }

    _play() {
      if (this.playing) return;
      this.playing = true;
      this.last = 0;
      this.raf = window.requestAnimationFrame(this._onTick);
    }

    _stop() {
      this.playing = false;
      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
        this.raf = null;
      }
    }

    _onTick(ts) {
      if (!this.playing) return;
      if (this.retracting) {
        // 回缩阶段：进度条正由 CSS 动画缩回 0，本阶段不推进进度
        this.raf = window.requestAnimationFrame(this._onTick);
        return;
      }

      if (!this.last) this.last = ts;
      var dt = ts - this.last;
      this.last = ts;
      if (!(dt > 0)) dt = 0;
      if (dt > MAX_TICK_MS) dt = MAX_TICK_MS;

      this.elapsed += dt;
      var total = this.duration * 1000;
      var p = total > 0 ? Math.min(1, this.elapsed / total) : 1;
      this.progress = p;
      this._syncProgress(p);
      this._setRailFill(p);

      if (p >= 1) this._completeItem();
      if (this.playing) this.raf = window.requestAnimationFrame(this._onTick);
    }

    /* ---------------------------------------------------------------- 切换 */
    _advance() {
      var n = this.count;
      if (n < 2) return;

      this.index = (this.index + 1) % n;
      this.elapsed = 0;
      this.progress = 0;

      // 循环归一：先把滑窗瞬时退回 n 格（三份内容周期重复 → 视觉完全一致），
      // 这样下面的位移永远只走一格
      if (this.t >= 2 * n) {
        this.t -= n;
        this._applyShift(true);
      }
      this.t += 1;

      this._setActive();
      this._syncText();
      this._syncDots();
      this._syncProgress(0);
      this._syncCenter();
      this._applyShift(false);
    }

    /* 本条播放满：①内容切到下一条 ②进度条从 100% 回缩到 0（时长 = retractMs），
       回缩完成后才开始新一条的进度增长（用户 2026-10-08 口径） */
    _completeItem() {
      this._advance(); // 内容切换（含 M 端圆点/滑轮/文案）
      this._retractFill(); // 进度条 100% → 0（此阶段不推进进度）
    }

    /* 进度条回缩：100% → 0（时长 = retractMs），完成后才开始新一条的进度增长 */
    _retractFill() {
      this.retracting = true;
      this.progress = 0;
      this.elapsed = 0;
      var fill = this.railFill;
      if (fill) {
        fill.style.transition = 'height ' + this.retractMs + 'ms cubic-bezier(0.4, 0, 0.2, 1)';
        fill.style.height = '0%';
      }
      var self = this;
      window.clearTimeout(this._retractTimer);
      this._retractTimer = window.setTimeout(function () {
        if (fill) fill.style.transition = '';
        self.retracting = false;
        self.last = 0; // 重新计时：下一条从 0 开始增长
        self._evaluate();
      }, this.retractMs + 20);
    }

    /* 带过渡地把滑窗移动 delta 格（正数＝上移，负数＝下移），并保持 active 落在中间格 */
    _moveBy(delta) {
      var n = this.count;
      // 安全网：一次位移不超过一圈（|delta| ≤ n−1），保证一次 ±n 归位即可落回有效区间
      if (delta > n - 1) delta = n - 1;
      if (delta < -(n - 1)) delta = -(n - 1);
      var maxT = COPY_COUNT * n - this.slots; // 有效渲染位置上限（窗口必须完全落在轨道内）
      if (this.t + delta > maxT) {
        this.t -= n;
        this._applyShift(true);
      } else if (this.t + delta < 0) {
        this.t += n;
        this._applyShift(true);
      }

      this.t += delta;
      this.elapsed = 0;
      this.progress = 0;
      this.last = 0;
      this._setActive();
      this._syncText();
      this._syncDots();
      this._syncProgress(0);
      this._syncCenter();
      this._applyShift(false);
    }

    _onResize() {
      var self = this;
      window.clearTimeout(this._resizeTimer);
      this._resizeTimer = window.setTimeout(function () {
        self._applyShift(true);
      }, SETTLE_MS);
    }

    /* ---------------------------------------------------------------- 同步 */
    _measureStep() {
      var kids = this.wheel ? this.wheel.children : [];
      if (kids.length < 2) return 0;
      var gap = kids[1].offsetTop - kids[0].offsetTop;
      return gap > 0 ? gap : 0;
    }

    _applyShift(instant) {
      if (!this.wheel) return;
      if (instant && !this.reduced) this.wheel.style.transition = 'none';
      var measured = this._measureStep();
      if (measured > 0) this.step = measured;
      this._applyShiftPx(-this.t * this.step);
      if (instant && !this.reduced) {
        void this.wheel.offsetHeight;
        this.wheel.style.transition = '';
      }
    }

    /* 直接写入位移（像素）：绕过格位换算（自动播放间隙/无过渡定位时用） */
    _applyShiftPx(px) {
      this.style.setProperty('--vl-eq-shift', px + 'px');
    }

    _setActive() {
      var kids = this.wheel ? this.wheel.children : [];
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        var dup = el.classList.contains('vl-eq__logo--dup');
        var on = !dup && parseInt(el.dataset.vlIndex, 10) === this.index;
        el.classList.toggle('is-active', on);
        if (dup) continue;
        // 键盘 / 读屏可感知的「当前项」（用户 2026-10-12）
        if (on) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      }
    }

    _syncCenter() {
      var kids = this.wheel ? this.wheel.children : [];
      var pos = this.t + this.mid;
      for (var i = 0; i < kids.length; i++) {
        kids[i].classList.toggle('is-center', i === pos);
      }
    }

    _syncText() {
      // M 端横向轨道落位（整数张）：-index×100% 由 CSS 计算；PC 仍用叠放显隐，不受影响
      this.style.setProperty('--vl-eq-slide-index', String(this.index));
      for (var i = 0; i < this.texts.length; i++) {
        var on = i === this.index;
        this.texts[i].classList.toggle('is-active', on);
        if (on) this.texts[i].removeAttribute('aria-hidden');
        else this.texts[i].setAttribute('aria-hidden', 'true');
      }
    }

    _syncDots() {
      for (var i = 0; i < this.dots.length; i++) {
        this.dots[i].classList.toggle('is-active', i === this.index);
      }
    }

    /* 竖向进度条（PC）：填充高度＝已播放占比（0…1） */
    _setRailFill(p) {
      if (!this.railFill) return;
      this.railFill.style.height = ((p > 0 ? p : 0) * 100) + '%';
    }

    _syncProgress(p) {
      var value = p > 0 ? p : 0;
      if (this._fillIndex !== this.index) {
        for (var i = 0; i < this.fills.length; i++) {
          if (this.fills[i] && i !== this.index) this.fills[i].style.width = '0%';
        }
        this._fillIndex = this.index;
      }
      var active = this.fills[this.index];
      if (active) active.style.width = value * 100 + '%';
    }
  }

  if (!window.customElements.get('vl-expert-quotes')) {
    window.customElements.define('vl-expert-quotes', VLExpertQuotes);
  }
})();
